import React, { useMemo } from 'react';
import { graphql, PageProps } from 'gatsby';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import Layout from 'src/elements/layout/layout';
import ResourceCenterArticle from 'src/organisms/articles/resourceCenter/resourceCenter.article';
import { ResouceCategoryArticleList, ResouceCategoryTemplateContainer, ResourceCategoryTemplateActions, ResourceCategoryTemplateContent } from './resourceCategory.styles';
import ResourceCenterMenu from 'src/organisms/articles/resourceCenter/resourceCenter.menu';
import Heading from 'src/elements/typography/heading';
import FadeInAnimationWrapper from 'src/elements/animation/fade-in/FadeInAnimationWrapper';
import LinkBranded from 'src/elements/links/Link.branded';

interface ResourceCategoryTemplatePageContextProps {
  category: string;
  subCategory?: string;
}

interface ResourceCategoryTemplateProps extends PageProps<unknown, ResourceCategoryTemplatePageContextProps> {
  data: {
    allContentfulResource: {
      edges: {
        node: {
          id: string;
          title: string;
          category: string;
          subCategory: string;
          slug: string;
          heroImage: { gatsbyImageData: IGatsbyImageData };
        }
      }[]
    }
  }
}

const ResourceCategoryTemplate = ({ pageContext, data, location }: ResourceCategoryTemplateProps): JSX.Element => {
  const articles = useMemo(() => {
    const allCategoryArticles = data.allContentfulResource.edges;

    if (pageContext.subCategory) {
      return allCategoryArticles.filter(article => article.node.subCategory === pageContext.subCategory);
    }

    return allCategoryArticles;
  }, [ data, pageContext ]);

  const pageName = pageContext.subCategory || pageContext.category;

  return (
    <Layout
      helmet={{
        title: `${ pageName } Archives | Mighty Networks`
      }}
      location={location}
    >
      <ResouceCategoryTemplateContainer>
        <ResourceCenterMenu />
        <ResourceCategoryTemplateContent>
          <FadeInAnimationWrapper>
            <Heading
              text={pageName}
              size="2.5rem"
              align="center"
            />
            <ResouceCategoryArticleList>
              {articles.filter(cS => cS.node.heroImage?.gatsbyImageData).map((article) => (
                <ResourceCenterArticle article={article.node} key={article.node.id} />
              ))}
            </ResouceCategoryArticleList>
          </FadeInAnimationWrapper>
          <ResourceCategoryTemplateActions>
            <FadeInAnimationWrapper>
              <LinkBranded
                size="1rem"
                text="Start Your 14-Day Free Trial"
                weight="bold"
                justify="center"
                url="https://new.mn.co/?plan=free-trial"
              />
            </FadeInAnimationWrapper>
          </ResourceCategoryTemplateActions>
        </ResourceCategoryTemplateContent>
      </ResouceCategoryTemplateContainer>
    </Layout>
  )
}

export default ResourceCategoryTemplate

export const pageQuery = graphql`
  query ResouceCategoryByCategoryName($category: String!) {
    allContentfulResource(filter: {category: {eq: $category}}, sort: {fields: createdAt, order: DESC}) {
      edges {
        node {
          id
          title
          slug
          contentful_id
          subCategory
          category
          heroImage {
            gatsbyImageData(width: 750, layout: CONSTRAINED, placeholder: BLURRED)
          }
        }
      }
    }
  }
`
